'use client'

import { useCallback, useEffect, useMemo, useRef } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Minus, X, ExternalLink, MessageCircle } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getMessages, type MessageRow } from '@/lib/messaging/message-actions'
import {
  markConversationRead,
  type ConversationListItem,
} from '@/lib/messaging/conversation-actions'
import { useSseChannel } from '@/lib/sse/use-sse-channel'
import { DefaultAvatar } from '@/components/ui/default-avatar'
import { MessageBubble } from './message-bubble'
import { Composer } from './composer'
import { refreshLatestMessages, invalidateConversationLists } from './message-cache'

interface DockChatWindowProps {
  conversation: ConversationListItem
  currentUserId: string
  minimized: boolean
  onToggleMinimize: () => void
  onClose: () => void
}

/**
 * LinkedIn-style docked mini-chat: a compact conversation window pinned to the
 * bottom-right so you can chat without leaving the page. Reuses MessageBubble +
 * Composer and shares the same React Query cache key as the full /messages
 * view, so messages/state stay consistent across both surfaces.
 */
export function DockChatWindow({
  conversation,
  currentUserId,
  minimized,
  onToggleMinimize,
  onClose,
}: DockChatWindowProps) {
  const conversationId = conversation.id
  const other = conversation.other_user
  const name = other.display_name ?? other.username ?? 'Conversation'
  const queryClient = useQueryClient()
  const scrollRef = useRef<HTMLDivElement>(null)
  const markedRef = useRef<string | null>(null)

  const query = useQuery<MessageRow[]>({
    queryKey: ['messages', conversationId],
    queryFn: async () => {
      const result = await getMessages({ conversationId })
      if (!result.success) throw new Error(result.error)
      return result.data ?? []
    },
    staleTime: 10_000,
    // Shares the ['messages', id] cache with the full-page ChatWindow; don't
    // refetch-on-mount or we'd replace (and drop) any history paged in there.
    // Freshness comes from the SSE merge below.
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })
  const messages = useMemo(() => query.data ?? [], [query.data])

  // Auto-scroll to the bottom as messages arrive (when expanded).
  useEffect(() => {
    if (minimized) return
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages.length, minimized])

  // Expanded + actually being viewed (visible or focused) → mark read. The
  // visibility guard mirrors ChatWindow so a backgrounded tab doesn't emit a
  // false "Seen".
  useEffect(() => {
    if (minimized) return
    if (document.visibilityState !== 'visible' && !document.hasFocus()) return
    const invalidate = () => invalidateConversationLists(queryClient)
    if (messages.length === 0) {
      void markConversationRead(conversationId).then(invalidate)
      return
    }
    const latest = messages[messages.length - 1].id
    if (latest.startsWith('optimistic-')) return
    if (latest === markedRef.current) return
    markedRef.current = latest
    void markConversationRead(conversationId, latest).then(invalidate)
  }, [messages, minimized, conversationId, queryClient])

  const onSse = useCallback(
    (event: { type: string }) => {
      if (event.type !== 'message') return
      void refreshLatestMessages(queryClient, conversationId)
    },
    [queryClient, conversationId],
  )
  useSseChannel(`messages:conversation:${conversationId}`, onSse, {
    enabled: !!conversationId,
  })

  return (
    <div className="flex w-80 flex-col overflow-hidden rounded-t-[var(--radius-lg)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] shadow-[var(--shadow-lg)]">
      {/* Header — clicking it toggles minimize */}
      <div className="flex items-center gap-2 border-b border-[var(--color-border-default)] px-3 py-2">
        <button
          type="button"
          onClick={onToggleMinimize}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          aria-label={minimized ? `Expand chat with ${name}` : `Minimize chat with ${name}`}
        >
          <div className="h-8 w-8 shrink-0 overflow-hidden rounded-full">
            {other.avatar_url ? (
              <Image src={other.avatar_url} alt="" width={32} height={32} className="h-full w-full object-cover" />
            ) : (
              <DefaultAvatar name={name} size={32} />
            )}
          </div>
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="truncate text-sm font-semibold text-[var(--color-text-primary)]">
              {name}
            </span>
            {other.username && (
              <span className="truncate text-[11px] text-[var(--color-text-muted)]">
                @{other.username}
              </span>
            )}
          </span>
        </button>
        <Link
          href={`/messages/${conversationId}`}
          aria-label="Open full conversation"
          title="Open full conversation"
          className="rounded p-1 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]"
        >
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </Link>
        <button
          type="button"
          onClick={onToggleMinimize}
          aria-label={minimized ? 'Expand' : 'Minimize'}
          className="rounded p-1 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]"
        >
          <Minus className="h-4 w-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close chat"
          className="rounded p-1 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {!minimized && (
        <>
          <div
            ref={scrollRef}
            role="log"
            aria-live="polite"
            className="h-80 space-y-2 overflow-y-auto bg-[var(--color-bg-page)] px-3 py-3"
          >
            {messages.length === 0 ? (
              <p className="mt-6 flex flex-col items-center gap-1 text-center text-xs text-[var(--color-text-muted)]">
                <MessageCircle className="h-5 w-5" aria-hidden="true" />
                No messages yet. Say hello.
              </p>
            ) : (
              messages.map((m) => (
                <MessageBubble
                  key={m.id}
                  message={m}
                  isOwn={m.sender_id === currentUserId}
                  conversationId={conversationId}
                  isPending={m.id.startsWith('optimistic-')}
                />
              ))
            )}
          </div>
          <Composer conversationId={conversationId} currentUserId={currentUserId} />
        </>
      )}
    </div>
  )
}
